import { orderBy, unionBy, uniqWith } from "lodash-es"
import { mountStoreDevtool } from "simple-zustand-devtools"
import AppToaster from "utils/AppToaster"
import { immer } from "utils/zustand.utils"
import create from "zustand"

type RouteType = {
  /**
   * In metres eg 5300 = 53 km
   */
  totalDistance: number
  /**
   * In minutes eg. 120 = 2 hours
   */
  totalDuration: number
  path: google.maps.LatLng[]
}
type RouteDataType = {
  id?: string
  category?: any
  name?: string
  colorCode?: any
  surveyArea?: Record<string, any>
  startDate?: string
  endTime?: Date
  startTime?: Date
  recurrence?: string
  asignee?: any
  selectedOutlets?: Record<string, any>[]
  outlets?: Record<string, any>[]
  step: "select" | "order" | "preview"
  optimizeRoute?: boolean
  route?: any
  googleMapRoute?: RouteType
  shouldTrack?: boolean
  userGroup?: any
  centeredAt?: number
}
type RouteStore = {
  data: RouteDataType
  toggleOutletSelect: (outlet: Record<string, any>) => void
  deSelectAll: () => void
  setData: (path: string, value: any) => void
  initialiseState: (data: RouteDataType) => void
  /**
   * @description Generates the preview route for the outlets
   */
  generatePreviewRoute: () => void
  nextStep: () => void
  prevStep: () => void
  sourceOutlet: () => Record<string, any>
  destinationOutlet: () => Record<string, any>
  allOutlets: (search?: string, clientSide?: boolean) => Record<string, any>[]
  querySelectedOutlets: (search?: string) => Record<string, any>[]
  reset: () => void
}

const INITIAL_DATA: RouteDataType = {
  selectedOutlets: [],
  outlets: [],
  step: "select",
  optimizeRoute: true,
  startTime: new Date(),
  endTime: new Date(),
  centeredAt: null,
}
const useRouteStore = create<RouteStore>(
  immer((set, get) => ({
    data: INITIAL_DATA,
    toggleOutletSelect(outlet) {
      set(prev => {
        let selected = prev.data.selectedOutlets
        // let outlets = prev.data.outlets
        const selectedOutletIndex = selected.findIndex(o => o.id === outlet.id)
        // const outletIndex = outlets.findIndex(o => o.id === outlet.id)
        const sequence = selected.length + 1

        if (selectedOutletIndex === -1) {
          // Outlet does not exist
          selected.push({ ...outlet, sequence: sequence })
          // outlets[outletIndex].sequence = sequence
        } else {
          // Outlet exists
          selected.splice(selectedOutletIndex, 1)
          // delete outlets[outletIndex].sequence
        }
      })
    },
    deSelectAll() {
      set(prev => {
        prev.data.selectedOutlets = []
        prev.data.googleMapRoute = null
      })
    },
    async generatePreviewRoute() {
      const source = get().sourceOutlet()
      const destination = get().destinationOutlet()

      if (!source && !destination) {
        return AppToaster.show({
          message: "Please select a source and destination outlet",
          intent: "danger",
        })
      }
      const sourceCoords = {
        lat: source.address.latitude,
        lng: source.address.longitude,
      }

      const destinationCoords = {
        lat: destination.address.latitude,
        lng: destination.address.longitude,
      }
      const outletWaypoints = get().data.selectedOutlets.filter(
        (_, i) => i !== 0 && i !== get().data.selectedOutlets.length - 1
      )
      const outletWaypointsLocation = outletWaypoints.map(outlet => ({
        location: new google.maps.LatLng(
          outlet.address.latitude,
          outlet.address.longitude
        ),
        stopover: true,
      }))
      try {
        const response = await new google.maps.DirectionsService().route({
          origin: sourceCoords,
          destination: destinationCoords,
          waypoints: outletWaypointsLocation,
          travelMode: google.maps.TravelMode.DRIVING,
          optimizeWaypoints: get().data.optimizeRoute,
        })
        if ((response as any).status === "OK") {
          const bestRoute = response.routes[0]
          let totalDistance = 0,
            totalDuration = 0

          for (let i = 0; i < bestRoute.legs.length; i++) {
            totalDistance += bestRoute.legs[i].distance.value
            totalDuration += bestRoute.legs[i].duration.value
          }
          const newOutletWaypoints = bestRoute.waypoint_order.map(
            point => outletWaypoints[point]
          )

          set(prev => {
            prev.data.selectedOutlets = uniqWith([
              source,
              ...newOutletWaypoints,
              destination,
            ])
            prev.data.googleMapRoute = {
              totalDistance,
              totalDuration,
              path: bestRoute.overview_path,
            }

            prev.data.centeredAt = Date.now()
          })
        } else {
          return AppToaster.show({
            message: "Unable to compute the route",
            intent: "danger",
          })
        }
      } catch ({ message }) {
        return AppToaster.show({
          message: message,
          intent: "danger",
        })
      }
    },
    setData(path, value) {
      set(prev => {
        if (path === "surveyArea" && prev.data.surveyArea?.id !== value.id) {
          prev.data.selectedOutlets = []
          prev.data.outlets = []
        }

        prev.data[path] = value

        if (path === "selectedOutlets") {
          prev.data.googleMapRoute = null
        }

        if (path === "outlets") {
          prev.data.centeredAt = Date.now()
        }
      })
    },
    initialiseState(value) {
      set(prev => {
        prev.data = value
      })
    },
    nextStep() {
      set(prev => {
        switch (prev.data.step) {
          case "select":
            prev.data.step = "order"
            break
          case "order":
            prev.data.step = "preview"
            break
          case "preview":
            break
        }
      })
    },
    prevStep() {
      set(prev => {
        switch (prev.data.step) {
          case "select":
            break
          case "order":
            prev.data.step = "select"
            break
          case "preview":
            prev.data.step = "order"
            break
        }
      })
    },
    sourceOutlet: () => get().data.selectedOutlets[0],
    querySelectedOutlets(search?: string) {
      const outlets = get().data.selectedOutlets
      if (search && get().data.step !== "select") {
        const searchRegex = new RegExp(search, "ig")
        return outlets.filter(
          outlet =>
            searchRegex.test(outlet.fullName) || searchRegex.test(outlet.number)
        )
      }
      return outlets
    },
    destinationOutlet() {
      const selectedOutlets = get().data.selectedOutlets
      return selectedOutlets[selectedOutlets.length - 1]
    },
    allOutlets(search, clientSide) {
      const selected = get().data.selectedOutlets.map((out, idx) => ({
        ...out,
        sequence: idx + 1,
      }))

      const outlets = get().data.outlets

      /**
       * @description If the user is searching through the list then we need to order the list
       * such as the queried outlets are always at the top
       */
      const orderedOutlets = orderBy(
        unionBy([...selected, ...outlets], "id"),
        "sequence",
        search ? "desc" : "asc"
      )
      if (clientSide && search) {
        const searchRegex = new RegExp(search, "ig")
        return orderedOutlets.filter(
          outlet =>
            searchRegex.test(outlet.fullName) || searchRegex.test(outlet.number)
        )
      }
      return orderedOutlets
    },
    reset() {
      set(prev => {
        prev.data = INITIAL_DATA as any
      })
    },
  }))
)
if (process.env.NODE_ENV === "development") {
  mountStoreDevtool("RouteStore", useRouteStore)
}
export default useRouteStore
